import { Common, Realty, services } from '@ambita/ambita-components-core';
import { TagInterface } from 'riot';
import { getAddressFromOwner } from '../../../services/cadastreReport';
import * as cadastreReportConstants from '../../../constants/cadastreReport';
import cadastreReportStore from '../../../stores/cadastreReport';
import { i18nMixin, I18nMixin } from '../../i18nMixin';

const { domUtils, filters } = services;
const { events: cadastreReportEvents } = cadastreReportConstants;

export interface SectionTag extends TagInterface, I18nMixin {
  data: any;
  isOwnersSectionVisible: boolean;
  showBuildingInfo: boolean;
  hasNoKindOfUnregisteredOwnerships: boolean;
  shouldCollapse(potentialArray: any | any[]): boolean;
  collapse(event: Event): void;
  formatNumber(number: number, decimals: number): string;
  booleanWriter(value: boolean): string;
  formatCurrency(amount: number): string;
  formatCadastre(cadastreIdent: Realty.CadastreIdent): string;
  isCadastreClickable(cadastreIdent: Realty.CadastreIdent): boolean;
  formatPostalAddress(street: Realty.ApiStreet): string;
  arrayTextWriter(array: any[]): string;
  getAddressFromOwner(owner: Realty.ApiPerson): string;
  getPlotsAreaNotes(): string;
  getCadastreIndustryGroup(): string;
  getBuildings(): any;
  getOwners(): any;
  getMainCadastreOwners(): any;
  getUnregisteredOwnerships(): any;
  getMainCadastreUnregisteredOwnerships(): any;
  getAllUnregisteredOwnerships(): any;
  getUnregisteredCadastreOwnerships(): any;
  getMainCadastreUnregisteredCadastreOwnerships(): any;
  getAllUnregisteredCadastreOwnerships(): any;
}

const hasWaterPlots = (cadastreIdent: Realty.CadastreIdent): boolean => {
  return cadastreIdent.cadastralUnitNumber === 0 && cadastreIdent.unitNumber === 1;
};

const hasOwnerlessPlots = (cadastreIdent: Realty.CadastreIdent): boolean => {
  return cadastreIdent.cadastralUnitNumber === 0 && cadastreIdent.unitNumber === 0;
};

export const init = (tag: SectionTag): void => {
  tag.mixin(i18nMixin);

  tag.shouldCollapse = (potentialArray: any | any[]): boolean => {
    return potentialArray && potentialArray.length > 1;
  };

  tag.collapse = (event: Event): void => {
    const currentTarget = event.currentTarget as HTMLInputElement;
    const container = domUtils.findParent(currentTarget, 'ambita-cadastre-report__sub-section--collapsible');
    domUtils.classUtils.toggleClass(container, 'ambita-cadastre-report__sub-section--collapsed');
  };

  tag.formatNumber = (number: number, decimals = 0): string => {
    return (isNaN(number) || number === 0) ? '' : filters.number(number, decimals);
  };

  tag.booleanWriter = (value: boolean): string => {
    return value ? 'Ja' : 'Nei';
  };

  tag.formatCurrency = (amount: number): string => {
    return filters.currency(amount, '', 'prefix', 0);
  };

  tag.formatCadastre = (cadastreIdent: Realty.CadastreIdent): string => {
    const i18n = services.i18n.getCurrentTranslation();

    if (hasWaterPlots(cadastreIdent)) {
      return `${filters.padLeft(cadastreIdent.municipalityNumber + '', 4, '0')}/${i18n.propertyLookup.waterPlots}`;
    } else if (hasOwnerlessPlots(cadastreIdent)) {
      return `${filters.padLeft(cadastreIdent.municipalityNumber + '', 4, '0')}/${i18n.propertyLookup.ownerlessPlots}`;
    } else {
      return filters.cadastre(cadastreIdent);
    }
  };

  tag.isCadastreClickable = (cadastreIdent: Realty.CadastreIdent): boolean => {
    if (!tag.opts.hasCadastreClickHandler) {
      return false;
    }

    return !(hasWaterPlots(cadastreIdent) || hasOwnerlessPlots(cadastreIdent));
  };

  tag.formatPostalAddress = (street: Realty.ApiStreet): string => {
    if (!street || isNaN(street.postalCode) || !street.postalPlace) {
      return '';
    }

    return `${filters.padLeft(String(street.postalCode), 4, '0')} ${street.postalPlace}`;
  };

  tag.arrayTextWriter = (array: Common.CodeText[]): string => {
    return array.map((element) => {
      return element.text;
    }).join(', ');
  };

  tag.getAddressFromOwner = getAddressFromOwner;

  tag.getPlotsAreaNotes = (): string => {
    return tag.data && tag.data.plots && tag.arrayTextWriter(tag.data.plots.areaNotes);
  };

  tag.getCadastreIndustryGroup = (): string => {
    // TODO cadastre industry group needs to be shown here, but at the moment Realty API does not return it
    return '';
  };

  tag.getBuildings = (): any => {
    return tag.data && tag.data.buildings;
  };

  tag.getOwners = (): any => {
    return tag.data && tag.data.owners;
  };

  tag.getMainCadastreOwners = (): any => {
    return tag.data && tag.data.mainCadastre && tag.data.mainCadastre.owners ? tag.data.mainCadastre.owners : undefined;
  };

  tag.getUnregisteredOwnerships = (): any => {
    return tag.data && tag.data.unregisteredOwnerships;
  };

  tag.getMainCadastreUnregisteredOwnerships = (): any => {
    return tag.data && tag.data.mainCadastre && tag.data.mainCadastre.unregisteredOwnerships ?
      tag.data.mainCadastre.unregisteredOwnerships : undefined;
  };

  tag.getAllUnregisteredOwnerships = (): any => {
    return tag.getUnregisteredOwnerships() || tag.getMainCadastreUnregisteredOwnerships();
  };

  tag.getUnregisteredCadastreOwnerships = (): any => {
    return tag.data && tag.data.unregisteredCadastreOwnerships;
  };

  tag.getMainCadastreUnregisteredCadastreOwnerships = (): any => {
    return tag.data && tag.data.mainCadastre && tag.data.mainCadastre.unregisteredCadastreOwnerships ?
      tag.data.mainCadastre.unregisteredCadastreOwnerships : undefined;
  };

  tag.getAllUnregisteredCadastreOwnerships = (): any => {
    return tag.getUnregisteredCadastreOwnerships() || tag.getMainCadastreUnregisteredCadastreOwnerships();
  };

  const updateState = (): void => {
    const { data } = cadastreReportStore.getState();
    tag.data = data;

    const buildings = tag.getBuildings();
    const owners = tag.getOwners();
    const mainCadastreOwners = tag.getMainCadastreOwners();
    const allUnregisteredOwnerships = tag.getAllUnregisteredOwnerships();
    const allUnregisteredCadastreOwnerships = tag.getAllUnregisteredCadastreOwnerships();
    const hasOwners = owners && owners.length > 0;
    const hasMainCadastreOwners = mainCadastreOwners && mainCadastreOwners.length > 0;
    const hasAnyUnregisteredOwnerships = allUnregisteredOwnerships && allUnregisteredOwnerships.length > 0;
    const hasAnyUnregisteredCadastreOwnerships = allUnregisteredCadastreOwnerships &&
      allUnregisteredCadastreOwnerships.length > 0;

    tag.showBuildingInfo = !buildings || buildings.length === 0;
    tag.isOwnersSectionVisible = hasOwners || hasMainCadastreOwners || hasAnyUnregisteredOwnerships;
    tag.hasNoKindOfUnregisteredOwnerships = !hasAnyUnregisteredOwnerships && !hasAnyUnregisteredCadastreOwnerships;

    tag.update();
  };

  tag.on('before-mount', updateState);

  tag.on('mount', () => {
    cadastreReportStore.on(cadastreReportEvents.CADASTRE_LOOKUP_LOADED, updateState);
  });

  tag.on('unmount', () => {
    cadastreReportStore.off(cadastreReportEvents.CADASTRE_LOOKUP_LOADED, updateState);
  });
};
